import React from 'react'
import ContentProgressPanel from '../panels/ContentProgressPanel'
import DailyActivityBoard from '../panels/DailyActivityBoard'
import MetricsOverviewPanel from '../panels/MetricsOverviewPanel'
import MonthlyActivityBoard from '../panels/MonthlyActivityBoard'
import CarePlanEditor from '../panels/carePlanEditor/CarePlanEditor'
import PlanViewer from '../panels/carePlanEditor/PlanViewer'
import ParticipantHistoryPanel from '../panels/patientHistory/ParticipantHistoryPanel'
import BasicDetailsPanel from '../panels/patientRecord/BasicDetailsPanel'
import BillingInfoPanel from '../panels/patientRecord/BillingInfoPanel'
import ConsentDocumentsPanel from '../panels/patientRecord/ConsentDocumentsPanel'
import EducationPanel from '../panels/patientRecord/EducationPanel'
import EmergencyContactPanel from '../panels/patientRecord/EmergencyContactPanel'
import EthnicityPanel from '../panels/patientRecord/EthnicityPanel'
import LocationCredentialingPanel from '../panels/patientRecord/LocationCredentialingPanel'
import OccupationPanel from '../panels/patientRecord/OccupationPanel'
import PrimaryCareProviderPanel from '../panels/patientRecord/PrimaryCareProviderPanel'
import ShippingInfoPanel from '../panels/patientRecord/ShippingInfoPanel'
import ReflectionsPanel from '../panels/ReflectionsPanel'
import TrackProgressPanel from '../panels/TrackProgressPanel'
import WeeklyActivityBoard from '../panels/WeeklyActivityBoard'
import { ProviderRoleId } from '../currentRole'
import { PanelChrome } from './PanelFrame'
import { PanelSpan } from './spans'

export type PanelGroup = 'block' | 'stat'

// Independent of `group`; care-plan is split because Care Plan and Progress admit different editors.
export type PanelCategory =
  | 'medicalRecord'
  | 'program'
  | 'carePlanViewer'
  | 'carePlanEditorPsychiatrist'
  | 'carePlanEditorOther'

export const CATEGORY_ORDER: PanelCategory[] = [
  'medicalRecord',
  'program',
  'carePlanViewer',
  'carePlanEditorPsychiatrist',
  'carePlanEditorOther',
]

export const PANEL_CATEGORY_LABELS: Record<PanelCategory, string> = {
  medicalRecord: 'Medical Record',
  program: 'Program',
  carePlanViewer: 'Care Plan Viewers',
  carePlanEditorPsychiatrist: 'Master Care Plan Editor',
  carePlanEditorOther: 'Care Plan Editors — Other Providers',
}

type PanelSpec = {
  id: string
  title: string
  group: PanelGroup
  category: PanelCategory
  defaultSpan: PanelSpan
  chrome: PanelChrome
  collapsible?: boolean
  // Omitted means every role; read through `isPanelAccessibleForRole`, never directly.
  allowedRoles?: ProviderRoleId[]
  Component: React.ComponentType
}

const ownRoleWithOversight = (role: ProviderRoleId): ProviderRoleId[] => [role, 'careLeadership', 'admin']

const PANEL_LIST = [
  {
    id: 'metricsOverview',
    title: 'Metrics',
    group: 'stat',
    category: 'program',
    defaultSpan: 'full',
    chrome: 'card',
    Component: MetricsOverviewPanel,
  },
  {
    id: 'trackProgress',
    title: 'Tracks',
    group: 'block',
    category: 'program',
    defaultSpan: 'full',
    chrome: 'card',
    Component: TrackProgressPanel,
  },
  {
    id: 'contentProgress',
    title: 'Content progress',
    group: 'block',
    category: 'program',
    defaultSpan: 'three-quarters',
    chrome: 'card',
    Component: ContentProgressPanel,
  },
  {
    id: 'reflections',
    title: 'Reflections',
    group: 'block',
    category: 'program',
    defaultSpan: 'quarter',
    chrome: 'card',
    Component: ReflectionsPanel,
  },
  {
    id: 'weeklyActivityBoard',
    title: 'Weekly activity',
    group: 'block',
    category: 'program',
    defaultSpan: 'full',
    chrome: 'card',
    Component: WeeklyActivityBoard,
  },
  {
    id: 'dailyActivityBoard',
    title: 'Daily activity',
    group: 'block',
    category: 'program',
    defaultSpan: 'half',
    chrome: 'card',
    Component: DailyActivityBoard,
  },
  {
    id: 'monthlyActivityBoard',
    title: 'Monthly activity',
    group: 'block',
    category: 'program',
    defaultSpan: 'full',
    chrome: 'card',
    Component: MonthlyActivityBoard,
  },
  // One id per role: `reconcileLayout` refuses a duplicate id in a layout (ADR-045).
  {
    id: 'carePlanEditorPsychiatrist',
    title: 'Master care plan editor',
    group: 'block',
    category: 'carePlanEditorPsychiatrist',
    defaultSpan: 'full',
    chrome: 'card',
    allowedRoles: ownRoleWithOversight('psychiatrist'),
    Component: () => React.createElement(CarePlanEditor, { role: 'psychiatrist' }),
  },
  {
    id: 'carePlanEditorNutritionist',
    title: 'Nutritionist care plan editor',
    group: 'block',
    category: 'carePlanEditorOther',
    defaultSpan: 'full',
    chrome: 'card',
    allowedRoles: ownRoleWithOversight('nutritionist'),
    Component: () => React.createElement(CarePlanEditor, { role: 'nutritionist' }),
  },
  {
    id: 'carePlanEditorTherapist',
    title: 'Therapist care plan editor',
    group: 'block',
    category: 'carePlanEditorOther',
    defaultSpan: 'full',
    chrome: 'card',
    allowedRoles: ownRoleWithOversight('therapist'),
    Component: () => React.createElement(CarePlanEditor, { role: 'therapist' }),
  },
  {
    id: 'carePlanEditorCoach',
    title: 'Coach care plan editor',
    group: 'block',
    category: 'carePlanEditorOther',
    defaultSpan: 'full',
    chrome: 'card',
    allowedRoles: ownRoleWithOversight('coach'),
    Component: () => React.createElement(CarePlanEditor, { role: 'coach' }),
  },
  {
    id: 'carePlanEditorCareCoordinator',
    title: 'Care Coordinator care plan editor',
    group: 'block',
    category: 'carePlanEditorOther',
    defaultSpan: 'full',
    chrome: 'card',
    allowedRoles: ownRoleWithOversight('careCoordinator'),
    Component: () => React.createElement(CarePlanEditor, { role: 'careCoordinator' }),
  },
  // One id per plan-producing role (ADR-042); every role may view every provider's plan.
  {
    id: 'planViewerPsychiatrist',
    title: 'Master care plan viewer',
    group: 'block',
    category: 'carePlanViewer',
    defaultSpan: 'full',
    chrome: 'card',
    collapsible: true,
    Component: () => React.createElement(PlanViewer, { role: 'psychiatrist' }),
  },
  {
    id: 'planViewerNutritionist',
    title: 'Nutritionist plan viewer',
    group: 'block',
    category: 'carePlanViewer',
    defaultSpan: 'full',
    chrome: 'card',
    collapsible: true,
    Component: () => React.createElement(PlanViewer, { role: 'nutritionist' }),
  },
  {
    id: 'planViewerTherapist',
    title: 'Therapist plan viewer',
    group: 'block',
    category: 'carePlanViewer',
    defaultSpan: 'full',
    chrome: 'card',
    collapsible: true,
    Component: () => React.createElement(PlanViewer, { role: 'therapist' }),
  },
  {
    id: 'planViewerCoach',
    title: 'Coach plan viewer',
    group: 'block',
    category: 'carePlanViewer',
    defaultSpan: 'full',
    chrome: 'card',
    collapsible: true,
    Component: () => React.createElement(PlanViewer, { role: 'coach' }),
  },
  {
    id: 'planViewerCareCoordinator',
    title: 'Care Coordinator plan viewer',
    group: 'block',
    category: 'carePlanViewer',
    defaultSpan: 'full',
    chrome: 'card',
    collapsible: true,
    Component: () => React.createElement(PlanViewer, { role: 'careCoordinator' }),
  },
  {
    id: 'participantHistory',
    title: 'Participant history',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: ParticipantHistoryPanel,
  },
  {
    id: 'basicDetails',
    title: 'Basic details',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: BasicDetailsPanel,
  },
  {
    id: 'ethnicity',
    title: 'Ethnicity',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: EthnicityPanel,
  },
  {
    id: 'occupation',
    title: 'Occupation and employment',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: OccupationPanel,
  },
  {
    id: 'education',
    title: 'Education',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: EducationPanel,
  },
  {
    id: 'locationCredentialing',
    title: 'Location and credential information',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: LocationCredentialingPanel,
  },
  {
    id: 'shippingInfo',
    title: 'Shipping information',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: ShippingInfoPanel,
  },
  {
    id: 'emergencyContact',
    title: 'Emergency contact information',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: EmergencyContactPanel,
  },
  {
    id: 'billingInfo',
    title: 'Billing information',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: BillingInfoPanel,
  },
  {
    id: 'primaryCareProvider',
    title: 'Primary care provider',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: PrimaryCareProviderPanel,
  },
  {
    id: 'consentDocuments',
    title: 'Informed Consent Documents',
    group: 'block',
    category: 'medicalRecord',
    defaultSpan: 'full',
    chrome: 'card',
    Component: ConsentDocumentsPanel,
  },
] as const satisfies readonly PanelSpec[]

export type PanelId = (typeof PANEL_LIST)[number]['id']

export type PanelDefinition = PanelSpec & { id: PanelId }

export const PLAN_ROLES = ['psychiatrist', 'nutritionist', 'therapist', 'coach', 'careCoordinator'] as const

export type PlanRole = (typeof PLAN_ROLES)[number]

export const PLAN_PANELS: Record<PlanRole, { editor: PanelId; viewer: PanelId }> = {
  psychiatrist: { editor: 'carePlanEditorPsychiatrist', viewer: 'planViewerPsychiatrist' },
  nutritionist: { editor: 'carePlanEditorNutritionist', viewer: 'planViewerNutritionist' },
  therapist: { editor: 'carePlanEditorTherapist', viewer: 'planViewerTherapist' },
  coach: { editor: 'carePlanEditorCoach', viewer: 'planViewerCoach' },
  careCoordinator: { editor: 'carePlanEditorCareCoordinator', viewer: 'planViewerCareCoordinator' },
}

export const PANELS: Record<PanelId, PanelDefinition> = PANEL_LIST.reduce(
  (all, panel) => Object.assign(all, { [panel.id]: panel }),
  {} as Record<PanelId, PanelDefinition>
)

export const PANEL_IDS: readonly PanelId[] = PANEL_LIST.map((panel) => panel.id)

export const isPanelId = (value: unknown): value is PanelId => PANEL_IDS.includes(value as PanelId)

const allPanels: PanelDefinition[] = PANEL_IDS.map((id) => PANELS[id])

export const alternativesFor = (id: PanelId): PanelDefinition[] =>
  allPanels.filter((panel) => panel.group === PANELS[id].group)

export const panelsInCategory = (category: PanelCategory): PanelDefinition[] =>
  allPanels.filter((panel) => panel.category === category)

// Must be applied on every path a panel can reach a coach's screen — see dashboard/advanced/CLAUDE.md.
export const isPanelAccessibleForRole = (id: PanelId, role: ProviderRoleId): boolean => {
  const { allowedRoles } = PANELS[id]

  return !allowedRoles || allowedRoles.includes(role)
}

// What a page admits, from `dashboard/tabs.ts`'s `PAGE_LIBRARY_CATEGORIES` / `PROGRESS_EXCLUDED_CATEGORIES`.
export type PanelOfferScope = {
  onlyCategories?: PanelCategory[]
  excludeCategories?: PanelCategory[]
  // `program` panels read `api/selectors.ts` and throw on mount outside `progress`'s ready branch.
  excludeProgramPanels?: boolean
}

// The role gate and the page's own gate, together — every path that places or renders a panel uses this.
export const isPanelOfferable = (id: PanelId, role: ProviderRoleId, scope: PanelOfferScope): boolean => {
  const { category } = PANELS[id]

  if (!isPanelAccessibleForRole(id, role) || scope.excludeCategories?.includes(category)) {
    return false
  }

  if (scope.onlyCategories) {
    return scope.onlyCategories.includes(category)
  }

  return !scope.excludeProgramPanels || category !== 'program'
}
